import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GuidedInput } from "@/components/ui/guided-input";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Loader2, Building2, Palette, Menu, Bell, User } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { useBranding } from "@/contexts/BrandingContext";
import { WORKSPACES } from "@/lib/navRegistry";
import { cn } from "@/lib/utils";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DATE_FORMATS = ["DD MMM YYYY", "MMM DD, YYYY", "DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"];
const LOCALES = [["en-IN", "English (India)"], ["en-US", "English (US)"], ["en-GB", "English (UK)"]];
const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Sanskrit"];
const TIMEZONES = [["Asia/Kolkata", "Asia/Kolkata (IST)"], ["UTC", "UTC"], ["America/New_York", "America/New_York"], ["Europe/London", "Europe/London"]];

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

function GeneralTab() {
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useBranding();

  useEffect(() => { apiFetch("/org-settings").then(setOrg).catch((e) => toast.error(e.message)); }, []);
  if (!org) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  const set = (k, v) => setOrg({ ...org, [k]: v });
  const toggleDay = (d) => {
    const wd = org.working_days || [];
    set("working_days", wd.includes(d) ? wd.filter((x) => x !== d) : [...wd, d]);
  };
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/org-settings", { method: "PUT", body: JSON.stringify(org) });
      toast.success("Organisation settings saved");
      refresh();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Card className="max-w-3xl p-6 shadow-soft" data-testid="organisation-form">
      <div className="space-y-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <GuidedInput label="Organisation name" required dataTestId="org-name" value={org.name || ""} onChange={(e) => set("name", e.target.value)}
            helperText="Shown across the workspace and in reports." exampleText="Nirvana Academy" />
          <GuidedInput label="Short name" dataTestId="org-short-name" value={org.short_name || ""} onChange={(e) => set("short_name", e.target.value)}
            helperText="Used in compact navigation and small spaces." exampleText="Nirvana" />
        </div>
        <GuidedInput label="Tagline" dataTestId="org-tagline" value={org.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
          helperText="An optional line that captures your purpose." exampleText="Wisdom for everyday living" />
        <div className="grid gap-5 sm:grid-cols-2">
          <GuidedInput label="Website" dataTestId="org-website" value={org.website || ""} onChange={(e) => set("website", e.target.value)}
            helperText="Your primary public website." acceptedFormat="URL" exampleText="https://lalitanirvana.com" />
          <GuidedInput label="Support / contact email" dataTestId="org-email" type="email" value={org.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)}
            helperText="Where important system notices are sent." exampleText="team@nirvana.example" />
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Default time zone">
            <Select value={org.timezone || "Asia/Kolkata"} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger data-testid="org-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Default locale">
            <Select value={org.locale || "en-IN"} onValueChange={(v) => set("locale", v)}>
              <SelectTrigger data-testid="org-locale"><SelectValue /></SelectTrigger>
              <SelectContent>{LOCALES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Default language">
            <Select value={org.default_language || "English"} onValueChange={(v) => set("default_language", v)}>
              <SelectTrigger data-testid="org-language"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Default landing page">
            <Select value={org.default_landing || "/"} onValueChange={(v) => set("default_landing", v)}>
              <SelectTrigger data-testid="org-landing"><SelectValue /></SelectTrigger>
              <SelectContent>{WORKSPACES.map((w) => <SelectItem key={w.route} value={w.route}>{w.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Date format">
            <Select value={org.date_format || "DD MMM YYYY"} onValueChange={(v) => set("date_format", v)}>
              <SelectTrigger data-testid="org-date-format"><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_FORMATS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Time format">
            <Select value={org.time_format || "12h"} onValueChange={(v) => set("time_format", v)}>
              <SelectTrigger data-testid="org-time-format"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="12h">12-hour (8:00 AM)</SelectItem><SelectItem value="24h">24-hour (08:00)</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="First day of week">
            <Select value={org.first_day_of_week || "Monday"} onValueChange={(v) => set("first_day_of_week", v)}>
              <SelectTrigger data-testid="org-first-day"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Monday">Monday</SelectItem><SelectItem value="Sunday">Sunday</SelectItem><SelectItem value="Saturday">Saturday</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Default working days">
          <div className="flex flex-wrap gap-2" data-testid="org-working-days">
            {DAYS.map((d) => {
              const on = (org.working_days || []).includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`org-day-${d}`}
                  className={cn("rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                    on ? "border-accent bg-accent/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
                  {d}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="flex justify-end pt-1">
          <Button onClick={save} disabled={saving} data-testid="save-org">{saving ? "Saving…" : "Save Changes"}</Button>
        </div>
      </div>
    </Card>
  );
}

function SurfacePreview({ assets, name, short, productLabel, headerH, dark }) {
  const pal = dark
    ? { bg: "#0F1F18", card: "#13251D", border: "rgba(255,255,255,0.10)", text: "#F3F1EA", sub: "rgba(255,255,255,0.5)", box: "#1f6b4f", boxText: "#ffffff" }
    : { bg: "#FBF8F1", card: "#FFFFFF", border: "#E7E1D5", text: "#1F2A24", sub: "#6B7A72", box: "#14503C", boxText: "#ffffff" };
  const full = dark ? (assets.dark?.url || assets.full?.url) : (assets.light?.url || assets.full?.url);
  const mark = assets.mark?.url || full;
  const login = assets.login?.url || full;

  const Word = () => full
    ? <img src={full} alt="" style={{ maxHeight: headerH, width: "auto" }} className="object-contain" />
    : (
      <span className="flex flex-col leading-none">
        <span className="font-heading font-bold tracking-tight" style={{ color: pal.text, fontSize: 17 }}>{(short || "Nirvana").toUpperCase()}</span>
        <span className="mt-0.5 font-medium" style={{ color: dark ? "#D4AF37" : "#C5A059", fontSize: 11 }}>{productLabel || "ContentOS"}</span>
      </span>
    );
  const Mark = ({ s = 34 }) => mark
    ? <img src={mark} alt="" style={{ height: s, width: "auto", maxWidth: s + 12 }} className="object-contain" />
    : <span className="flex items-center justify-center rounded-md font-heading font-semibold"
        style={{ height: s, width: s, background: pal.box, color: pal.boxText, fontSize: s * 0.45 }}>{(short || "N").charAt(0).toUpperCase()}</span>;

  const cardStyle = { background: pal.card, border: `1px solid ${pal.border}` };
  return (
    <div className="overflow-hidden rounded-xl" style={{ background: pal.bg, border: `1px solid ${pal.border}` }}>
      <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide" style={{ color: pal.sub, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        {dark ? "Dark surface · deep green" : "Light surface · ivory"}
      </div>
      <div className="space-y-3 p-3">
        <div className="flex items-center justify-between rounded-lg px-3 py-2" style={cardStyle}>
          <Word />
          <div className="flex items-center gap-2" style={{ color: pal.sub }}><Menu className="h-4 w-4" /><Bell className="h-4 w-4" /><User className="h-4 w-4" /></div>
        </div>
        <div className="flex gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-lg px-3 py-2" style={cardStyle}><Mark /><Word /></div>
          <div className="flex items-center justify-center rounded-lg px-3 py-2" style={cardStyle}><Mark /></div>
        </div>
        <div className="flex flex-col items-center gap-2 rounded-lg py-4" style={cardStyle}>
          {login ? <img src={login} alt="" style={{ maxHeight: headerH + 8, width: "auto" }} className="object-contain" /> : <><Mark s={40} /><Word /></>}
          <span className="text-[11px]" style={{ color: pal.sub }}>Sign in to {name || "Nirvana"}</span>
        </div>
      </div>
    </div>
  );
}

function BrandingTab() {
  const { assets: ctxAssets, settings: ctxSettings, org_name, short_name, refresh } = useBranding();
  const [assets, setAssets] = useState(ctxAssets || {});
  const [sizing, setSizing] = useState({ header_height: 36, mobile_height: 28, product_label: "ContentOS", ...(ctxSettings || {}) });
  const [savingSize, setSavingSize] = useState(false);
  useEffect(() => { setAssets(ctxAssets || {}); }, [ctxAssets]);
  useEffect(() => { setSizing((s) => ({ ...s, ...(ctxSettings || {}) })); }, [ctxSettings]);

  const update = (type) => (asset) => {
    setAssets((a) => { const n = { ...a }; if (asset) n[type] = asset; else delete n[type]; return n; });
    refresh();
  };

  const saveSizing = async () => {
    setSavingSize(true);
    try {
      await apiFetch("/branding/settings", { method: "PUT", body: JSON.stringify(sizing) });
      toast.success("Branding settings saved");
      refresh();
    } catch (e) { toast.error(e.message); } finally { setSavingSize(false); }
  };

  const items = [
    ["full", "Primary full logo", "Main logo with wordmark. Shown in headers and expanded navigation."],
    ["mark", "Compact brand mark", "Square icon used in compact navigation and small spaces."],
    ["light", "Logo for light backgrounds", "Used automatically on light surfaces."],
    ["dark", "Logo for dark backgrounds", "Used automatically on dark surfaces (sidebar, dark mode)."],
    ["login", "Login screen logo", "Displayed on the sign-in screen."],
    ["favicon", "Favicon", "Browser tab icon. PNG, ICO or SVG."],
    ["app_icon", "App / PWA icon", "Installed-app icon. Use a square image, 512×512 recommended."],
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map(([type, label, hint]) => (
            <ImageUpload key={type} assetType={type} label={label} hint={hint}
              dark={type === "dark"} current={assets[type]} onChange={update(type)} />
          ))}
        </div>

        <Card className="p-5 shadow-soft" data-testid="branding-sizing">
          <div className="mb-1 text-sm font-medium">Safe logo sizing</div>
          <p className="mb-4 text-xs text-muted-foreground">
            Control how tall your logo appears. Aspect ratio is always preserved — logos are contained, never stretched or cropped.
          </p>
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span>Desktop header height</span>
                <span className="font-medium text-muted-foreground">{sizing.header_height}px</span>
              </div>
              <Slider min={20} max={56} step={2} value={[sizing.header_height]}
                onValueChange={([v]) => setSizing((s) => ({ ...s, header_height: v }))} data-testid="sizing-header" />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span>Mobile height</span>
                <span className="font-medium text-muted-foreground">{sizing.mobile_height}px</span>
              </div>
              <Slider min={18} max={44} step={2} value={[sizing.mobile_height]}
                onValueChange={([v]) => setSizing((s) => ({ ...s, mobile_height: v }))} data-testid="sizing-mobile" />
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={saveSizing} disabled={savingSize} data-testid="save-sizing">
                {savingSize ? "Saving…" : "Save sizing"}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-5 shadow-soft" data-testid="branding-text-fallback">
          <div className="mb-1 text-sm font-medium">Text fallback</div>
          <p className="mb-4 text-xs text-muted-foreground">
            Shown when no image logo is set. The brand name comes from your Short Name (<strong>{short_name}</strong>), rendered as <strong>{(short_name || "Nirvana").toUpperCase()}</strong>. The product label below is configurable.
          </p>
          <div className="mb-4 max-w-xs">
            <label className="mb-1.5 block text-sm font-medium">Product label</label>
            <Input value={sizing.product_label || ""} onChange={(e) => setSizing((s) => ({ ...s, product_label: e.target.value }))}
              placeholder="ContentOS" data-testid="product-label-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[false, true].map((dark) => (
              <div key={String(dark)} className="flex flex-col items-center justify-center rounded-lg py-5"
                style={{ background: dark ? "#0F1F18" : "#FBF8F1", border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "#E7E1D5"}` }}
                data-testid={`text-fallback-${dark ? "dark" : "light"}`}>
                <span className="font-heading text-xl font-bold tracking-tight" style={{ color: dark ? "#E8EFEA" : "#1F2A24" }}>
                  {(short_name || "Nirvana").toUpperCase()}
                </span>
                <span className="mt-0.5 text-xs font-medium" style={{ color: dark ? "#D4AF37" : "#C5A059" }}>
                  {sizing.product_label || "ContentOS"}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={saveSizing} disabled={savingSize} data-testid="save-product-label">
              {savingSize ? "Saving…" : "Save text fallback"}
            </Button>
          </div>
        </Card>
      </div>

      <div className="space-y-4">
        <div className="text-sm font-medium">Live preview</div>
        <SurfacePreview assets={assets} name={org_name} short={short_name} productLabel={sizing.product_label} headerH={sizing.header_height} dark={false} />
        <SurfacePreview assets={assets} name={org_name} short={short_name} productLabel={sizing.product_label} headerH={sizing.header_height} dark={true} />
        <p className="text-xs text-muted-foreground">
          On desktop, when no Primary Full Logo is uploaded, the two-line <strong>{(short_name || "Nirvana").toUpperCase()} / {sizing.product_label || "ContentOS"}</strong> lockup is shown (never the compact mark). Supported: PNG, JPG, WebP, ICO, SVG. Max 5&nbsp;MB.
        </p>
      </div>
    </div>
  );
}

export default function Organisation() {
  return (
    <div className="animate-fade-up">
      <PageHeader eyebrow="Settings" title="Organisation" subtitle="Core details and branding for your Nirvana workspace." />
      <Tabs defaultValue="general">
        <TabsList className="mb-6" data-testid="org-subtabs">
          <TabsTrigger value="general" data-testid="org-tab-general"><Building2 className="mr-1.5 h-4 w-4" /> General</TabsTrigger>
          <TabsTrigger value="branding" data-testid="org-tab-branding"><Palette className="mr-1.5 h-4 w-4" /> Branding</TabsTrigger>
        </TabsList>
        <TabsContent value="general"><GeneralTab /></TabsContent>
        <TabsContent value="branding"><BrandingTab /></TabsContent>
      </Tabs>
    </div>
  );
}
